import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * COMPONENTE: PluckingRooster OVERLAY (DESIGN / CHILD VISUAL)
 * ------------------------------------------------------------------
 * Este componente maneja la visualización e interactividad del Overlay
 * de "Aplasta las moras" (PluckingRooster).
 * Soporta desestructuración directa (OBS Overlay) y la lectura unificada
 * del prop 'data' (Legacy Screen del Estadio) para evitar errores del 'map'.
 * ------------------------------------------------------------------
 */
export function PluckingRooster(props) {
    const { 
        data, 
        flowState: propFlowState, 
        offsetY: propOffsetY, 
        bannerMode: propBannerMode, 
        winnerName: propWinnerName, 
        rankingList: propRankingList, 
        showConfetti: propShowConfetti 
    } = props;

    // Estados de soporte local para cuando se inyecta desde la pantalla del estadio (Case 19)
    const [localRankingList, setLocalRankingList] = useState([]);
    const [localOffsetY, setLocalOffsetY] = useState(0);
    const [localBannerMode, setLocalBannerMode] = useState('ranking'); // Por defecto modo tabla en estadio
    const [localWinnerName, setLocalWinnerName] = useState('');
    const [localFlowState, setLocalFlowState] = useState('idle');
    const [localShowConfetti, setLocalShowConfetti] = useState(false);

    useEffect(() => {
        if (data) {
            const { subtype, data: payload } = data;
            if (!subtype) return;

            switch (subtype) {
                case 'show_ranking':
                    if (payload) {
                        setLocalRankingList(payload.rankingList || []);
                        setLocalFlowState('visible');
                        setLocalWinnerName(payload.winnerName || '');
                    }
                    break;
                case 'hide_ranking':
                    setLocalFlowState('idle');
                    break;
                case 'update_data':
                    if (payload) {
                        setLocalRankingList(payload.rankingList || []);
                        setLocalWinnerName(payload.winnerName || '');
                    }
                    break;
                case 'config_layout':
                    if (payload) {
                        if (payload.positionOffset !== undefined) setLocalOffsetY(payload.positionOffset);
                        if (payload.layoutMode === 1) setLocalBannerMode('ranking');
                        else setLocalBannerMode('invitation');
                    }
                    break;
                default:
                    break;
            }
        }
    }, [data]);

    // Selección inteligente de datos según el origen del renderizado
    const isFromProps = data === undefined;
    const rankingList = isFromProps ? (propRankingList || []) : localRankingList;
    const flowState = isFromProps ? propFlowState : localFlowState;
    const offsetY = isFromProps ? propOffsetY : localOffsetY;
    const bannerMode = isFromProps ? propBannerMode : localBannerMode;
    const winnerName = isFromProps ? propWinnerName : localWinnerName;
    const showConfetti = isFromProps ? propShowConfetti : localShowConfetti;

    if (flowState === 'idle') return null;

    const qrValue = `${window.location.origin}/rooster?s=obs`;

    return (
        <div className="overlayquizz overlayquizz--rooster" style={{ bottom: `${offsetY}px` }}>
            {/* Confeti CSS acelerado por GPU */}
            {showConfetti && bannerMode === 'winner' && (
                <div className="overlayquizz-confetti">
                    {[...Array(40)].map((_, i) => (
                        <div key={i} className="overlayquizz-confetti__piece" />
                    ))}
                </div>
            )}

            <div className={`overlayquizz__banner overlayquizz__banner--${flowState}`}>
                {bannerMode === 'invitation' ? (
                    <div className='overlayquizz__banner--v2'>
                        {/* Banner Cintillo (Imágenes compartidas de la trivia) */}
                        <img src='/img/overlays/rooster/chivas-varonil/cintillo-1536-x-180-px.png' className='overlayquizz__banner--v2__Image--Md' alt="Banner Dale un chivazo" />
                        <img src='/img/overlays/rooster/chivas-varonil/cintillo-1920-x-180-px.png' className='overlayquizz__banner--v2__Image--Lg' alt="Banner Dale un chivazo Lg" />
                        
                        {/* Código QR Izquierdo */}
                        <div className='overlayquizz__banner--v2__QR-Overlay overlayquizz__banner--v2__QR-Overlay--left'>
                            <QRCodeSVG 
                                value={qrValue} 
                                size={256}
                                style={{ width: '92%', height: '92%', display: 'block' }}
                                bgColor={"#d9d9d9"} 
                                fgColor={"#000000"} 
                                level={"H"} 
                                imageSettings={{
                                    src: "/img/overlays/rubaquiz/interaktia-transparent.png",
                                    x: undefined,
                                    y: undefined,
                                    height: 52,
                                    width: 52,
                                    excavate: true, 
                                }}
                            />
                        </div>
                    </div>
                ) : bannerMode === 'winner' ? (
                    <div className='overlayquizz__banner__winner' style={{ backgroundColor: '#23214a' }}>
                        {/* Logo Izquierdo */}
                        <div className="overlayquizz__logo overlayquizz__logo--left">
                            <img src="/img/overlays/rubaquiz/interaktia-barrio9.png" alt="Logo Left" style={{ height: '180px' }} />
                        </div>

                        {/* Anuncio del Ganador */}
                        <div className="overlayquizz__content">
                            <p className="overlayquizz__subtitle animate-pulse mb-1 text-uppercase" style={{ color: '#ecaa1f', fontFamily: 'Montserrat, sans-serif' }}>
                                🎉 ¡Tenemos ganador(a) de Dale un chivazo 🎉
                            </p>
                            <h2 className="overlayquizz__title text-uppercase" style={{ color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontWeight: 900 }}>
                                {winnerName || 'Y el ganador es...'}
                            </h2>
                        </div>

                        {/* Logo Derecho */}
                        <div className="overlayquizz__logo overlayquizz__logo--right">
                            <img src="/img/overlays/rubaquiz/interaktia-barrio9.png" alt="Logo Right" style={{ height: '180px' }} />
                        </div>
                    </div>
                ) : (
                    // Modo ranking original: Muestra la lista con los mejores 5 puntajes
                    <div className="w-100 d-flex justify-content-center p-3">
                        <div className="rooster-overlay rooster-overlay--visible rooster-overlay--vertical" style={{ position: 'relative', width: '700px' }}>
                            <div className="rooster-overlay__header">
                                <p className="rooster-overlay__title">Ranking Dale un chivazo</p>
                            </div>

                            <div className="rooster-overlay__list">
                                {rankingList.map((player, index) => (
                                    (index < 5) && (
                                        <div 
                                            key={index} 
                                            className="rooster-overlay__item" 
                                            style={{ animationDelay: `${index * 0.1}s`, opacity: 1, transform: 'translateX(0)' }}
                                        >
                                            <div className={`rooster-overlay__rank rank-${index + 1}`}>
                                                #{index + 1}
                                            </div>
                                            <div className="rooster-overlay__info">
                                                <span className="rooster-overlay__name">{player.fullName || player.name}</span>
                                            </div>
                                            <div className="rooster-overlay__score">
                                                {player.score} pts
                                            </div>
                                        </div>
                                    )
                                ))}

                                {rankingList.length === 0 && (
                                    <div className="rooster-overlay__empty">
                                        Esperando resultados...
                                    </div>
                                )}
                                <p className='text-center text-white mb-0 mt-3 small opacity-50'>Hecho con ♥ por Interaktia | 2026</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PluckingRooster;